import { AlertTriangle, Send } from 'lucide-react';

import { Button } from '@/ui/base/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import { EmptyState } from '@/ui/EmptyState';
import { GlossaryTerm } from '@/ui/GlossaryTerm';

import type { DreEmDefasagem as DreEmDefasagemLinha } from '../contrato';

import { LinhaDeDre } from './LinhaDeDre';
import { SemRecortePorDre } from './SemRecortePorDre';

/**
 * "DREs em defasagem" — as diretorias cujo atraso já trava o ciclo financeiro.
 *
 * O título leva o glossário (`DRE_DEFASADA`): "defasagem" tem corte definido —
 * lote parado além do limite sem envio, devolução ou complementação —, e quem
 * lê o painel precisa alcançar esse corte sem sair da tela.
 */

interface DresEmDefasagemProps {
  dres: DreEmDefasagemLinha[];
  /** `false` esconde as cobranças — quem não pode cobrar não vê botão nenhum. */
  podeCobrar: boolean;
  /**
   * `true` para quem não é SME: a lista compara diretorias e o sistema ainda não
   * sabe de qual diretoria é quem está olhando (ver `SemRecortePorDre`).
   */
  semRecortePorDre: boolean;
  aoCobrar: (dre: DreEmDefasagemLinha) => void;
  aoCobrarTodas: () => void;
}

export function DresEmDefasagem({
  dres,
  podeCobrar,
  semRecortePorDre,
  aoCobrar,
  aoCobrarTodas,
}: DresEmDefasagemProps) {
  return (
    <Card className="rounded-2xl border border-border/60">
      <CardHeader className="pb-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <AlertTriangle className="h-4 w-4 text-destructive" aria-hidden="true" />
            <GlossaryTerm termo="DRE_DEFASADA">DREs em defasagem</GlossaryTerm>
          </CardTitle>
          {podeCobrar && !semRecortePorDre && dres.length > 0 && (
            <Button variant="outline" size="sm" onClick={aoCobrarTodas}>
              <Send className="mr-1.5 h-4 w-4" aria-hidden="true" />
              Cobrar todas ({dres.length})
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {semRecortePorDre ? (
          <SemRecortePorDre />
        ) : dres.length === 0 ? (
          <EmptyState
            variant="success"
            title="Nenhuma DRE em defasagem"
            description="Nenhuma diretoria está com lote parado além do prazo nesta competência."
          />
        ) : (
          dres.map((dre) => (
            <LinhaDeDre
              key={dre.dreId}
              dre={dre}
              tom={dre.critica ? 'critico' : 'atencao'}
              podeCobrar={podeCobrar}
              aoCobrar={aoCobrar}
            />
          ))
        )}
      </CardContent>
    </Card>
  );
}
